import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "@/lib/api";
import { LEGAL_VERSION } from "@/lib/legal";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [legalVersion, setLegalVersion] = useState(LEGAL_VERSION);
  const legalVersionRef = useRef(LEGAL_VERSION);

  const fetchLegalVersion = useCallback(async () => {
    try {
      const { data } = await api.get("/legal/config", { skipAuthRedirect: true });
      const nextVersion = String(data?.legal_version || "").trim();
      if (nextVersion) {
        legalVersionRef.current = nextVersion;
        setLegalVersion(nextVersion);
        return nextVersion;
      }
    } catch {
      // Keep the embedded version as a safe fallback if the same backend that
      // will handle the next request is temporarily unreachable.
    }
    return legalVersionRef.current || LEGAL_VERSION;
  }, []);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("cs_token");
    const legalVersionPromise = fetchLegalVersion();
    if (!token) {
      await legalVersionPromise;
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const [{ data }] = await Promise.all([
        api.get("/auth/me"),
        legalVersionPromise,
      ]);
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [fetchLegalVersion]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("cs_token", data.access_token);
    await fetchMe();
  }, [fetchMe]);

  const register = useCallback(async (email, password, name, acceptance) => {
    const currentLegalVersion = await fetchLegalVersion();
    const { data } = await api.post("/auth/register", {
      email,
      password,
      name,
      legal_version: currentLegalVersion,
      privacy_notice_acknowledged: Boolean(acceptance?.privacy),
      beta_terms_accepted: Boolean(acceptance?.terms),
      age_professional_confirmed: Boolean(acceptance?.professional),
    });
    return data;
  }, [fetchLegalVersion]);

  const googleAuth = useCallback(async (credential, acceptance) => {
    const currentLegalVersion = await fetchLegalVersion();
    const { data } = await api.post("/auth/google", {
      credential,
      legal_version: currentLegalVersion,
      privacy_notice_acknowledged: Boolean(acceptance?.privacy),
      beta_terms_accepted: Boolean(acceptance?.terms),
      age_professional_confirmed: Boolean(acceptance?.professional),
    });
    if (data.access_token) {
      localStorage.setItem("cs_token", data.access_token);
      await fetchMe();
    }
    return data;
  }, [fetchMe, fetchLegalVersion]);

  const acceptLegal = useCallback(async (acceptance) => {
    const submitAcceptance = async (version) => api.post("/account/legal-acceptance", {
      legal_version: version,
      privacy_notice_acknowledged: Boolean(acceptance?.privacy),
      beta_terms_accepted: Boolean(acceptance?.terms),
      age_professional_confirmed: Boolean(acceptance?.professional),
    });

    let currentLegalVersion = await fetchLegalVersion();
    try {
      const { data } = await submitAcceptance(currentLegalVersion);
      setUser(data);
      return data;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code !== "legal_version_outdated" || !detail?.legal_version) throw error;

      // A deploy can change the legal version while this page is already open.
      // Retry once with the server-declared version instead of telling the user
      // that an already checked confirmation is missing.
      currentLegalVersion = String(detail.legal_version).trim();
      legalVersionRef.current = currentLegalVersion;
      setLegalVersion(currentLegalVersion);
      const { data } = await submitAcceptance(currentLegalVersion);
      setUser(data);
      return data;
    }
  }, [fetchLegalVersion]);

  const linkGoogle = useCallback(async (credential, password) => {
    const { data } = await api.post("/auth/google/link", { credential, password });
    localStorage.setItem("cs_token", data.access_token);
    await fetchMe();
    return data;
  }, [fetchMe]);

  const logout = useCallback(() => {
    localStorage.removeItem("cs_token");
    setUser(null);
    window.location.href = "/login";
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      legalVersion,
      login,
      register,
      googleAuth,
      acceptLegal,
      linkGoogle,
      logout,
      refreshUser: fetchMe,
      refreshLegalVersion: fetchLegalVersion,
    }),
    [user, loading, legalVersion, login, register, googleAuth, acceptLegal, linkGoogle, logout, fetchMe, fetchLegalVersion]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
